import React, { useState, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Paper,
  Stack,
  Chip,
  Card,
  CardContent,
  Alert,
  Snackbar,
  CircularProgress,
  IconButton,
  TextField,
  Divider
} from '@mui/material';
import {
  Close as CloseIcon,
  Save as SaveIcon,
  Comment as CommentIcon,
  Article as ArticleIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Highlight from '@tiptap/extension-highlight';
import Link from '@tiptap/extension-link';
import { alpha } from '@mui/material/styles';

// Types - Using LiteratureCase type for compatibility
import { LiteratureCase } from '../services/caseManagementApi';

interface ArticleReviewEditorProps {
  open: boolean;
  onClose: () => void;
  caseData: LiteratureCase | null;
  onSave: (data: Partial<LiteratureCase>) => Promise<void>;
  onAddComment: (caseId: number, comment: string) => Promise<any>;
  onLoadComments: (caseId: number) => Promise<any[]>;
}

interface SnackbarState {
  open: boolean;
  message: string;
  severity: 'success' | 'error' | 'warning' | 'info';
}

const ArticleReviewEditor: React.FC<ArticleReviewEditorProps> = ({
  open,
  onClose,
  caseData,
  onSave,
  onAddComment,
  onLoadComments
}) => {
  const [editorValue, setEditorValue] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [comments, setComments] = useState<any[]>([]);
  const [newComment, setNewComment] = useState('');
  const [snackbar, setSnackbar] = useState<SnackbarState>({
    open: false,
    message: '',
    severity: 'info'
  });
  const [autoSaveEnabled, setAutoSaveEnabled] = useState<boolean>(true);
  const autoSaveTimerRef = useRef<any>(null);
  const currentCaseIdRef = useRef<number | null>(caseData ? caseData.id : null);
  useEffect(() => { currentCaseIdRef.current = caseData ? caseData.id : null; }, [caseData]);

  const editor = useEditor({
    extensions: [
      StarterKit,
      Highlight,
      Link.configure({ openOnClick: true })
    ],
    content: '',
    autofocus: false,
    onUpdate: ({ editor }) => {
      if (suppressAutosaveRef.current) return;
      const html = editor.getHTML();
      setEditorValue(html);
      if (autoSaveEnabled && currentCaseIdRef.current) {
        try {
          localStorage.setItem(`articleReviewHTML:${currentCaseIdRef.current}`, html);
        } catch {}
        if (autoSaveTimerRef.current) {
          clearTimeout(autoSaveTimerRef.current);
        }
        autoSaveTimerRef.current = setTimeout(async () => {
          try {
            await onSave({
              id: currentCaseIdRef.current as number,
              // @ts-ignore
              review_html: html,
              case_narrative: htmlToPlainText(html) || html,
            });
          } catch (e) {
            // silent fail for autosave; a manual Save still shows snackbar
          }
        }, 800);
      }
    },
  });

  const showSnackbar = (message: string, severity: SnackbarState['severity'] = 'info') => {
    setSnackbar({ open: true, message, severity });
  };

  const localStorageKey = caseData ? `articleReviewHTML:${caseData.id}` : '';
  const suppressAutosaveRef = useRef<boolean>(false);

  const htmlToPlainText = (html: string) => {
    const div = document.createElement('div');
    div.innerHTML = html;
    return div.textContent || div.innerText || '';
  };

  const loadArticleData = async () => {
    if (!caseData) return;

    setLoading(true);
    try {
      let fullArticleText = '';

      // 1) Prefer locally saved review HTML for instant persistence
      try {
        if (caseData && localStorageKey) {
          const savedHtml = localStorage.getItem(localStorageKey);
          if (savedHtml) {
            suppressAutosaveRef.current = true;
            setEditorValue(savedHtml);
            editor?.commands.setContent(savedHtml);
            suppressAutosaveRef.current = false;
            // Load comments after setting content
            try {
              const caseComments = await onLoadComments(caseData.id);
              setComments(caseComments);
            } catch (error) {
              console.warn('Could not load comments:', error);
              setComments([]);
            }
            setLoading(false);
            return;
          }
        }
      } catch {}

      // Try to get detailed case data first (also check server review_html)
      try {
        const { getCaseDetail } = await import('../services/caseManagementApi');
        const caseDetail = await getCaseDetail(caseData.id);
        const serverHtml = (caseDetail?.case && caseDetail.case.review_html) || caseDetail.review_html;
        if (serverHtml && typeof serverHtml === 'string' && serverHtml.trim().length > 0) {
          suppressAutosaveRef.current = true;
          setEditorValue(serverHtml);
          editor?.commands.setContent(serverHtml);
          suppressAutosaveRef.current = false;
          // Load comments
          try {
            const caseComments = await onLoadComments(caseData.id);
            setComments(caseComments);
          } catch (error) {
            console.warn('Could not load comments:', error);
            setComments([]);
          }
          setLoading(false);
          return;
        }
        // If no server HTML, check localStorage per-case key
        try {
          const localHtml = localStorage.getItem(`articleReviewHTML:${caseData.id}`);
          if (localHtml) {
            setEditorValue(localHtml);
            editor?.commands.setContent(localHtml);
            const caseComments = await onLoadComments(caseData.id);
            setComments(caseComments);
            setLoading(false);
            return;
          }
        } catch {}

        const articleTextSources = [
          caseDetail.article_text,
          caseDetail.pdf_text,
          caseDetail.full_text,
          caseDetail.content,
          caseDetail.article_snippet,
          caseDetail.snippet,
          caseDetail.abstract,
          caseDetail.case_narrative
        ].filter(Boolean);

        if (articleTextSources.length > 0) {
          fullArticleText = articleTextSources.join('\n\n');
        }
      } catch (error) {
        console.warn('Could not fetch detailed case data:', error);
      }
      
      // Fallback to available data if no full article text
      if (!fullArticleText) {
        const fallbackSources = [
          caseData.case_narrative
        ].filter(Boolean);
        
        fullArticleText = fallbackSources.join('\n\n---\n\n');
      }
      
      // Prefer plain text to start; convert to simple paragraphs
      const html = fullArticleText
        .split('\n')
        .map(line => `<p>${line.replace(/</g, '&lt;').replace(/>/g, '&gt;')}</p>`) 
        .join('');
      suppressAutosaveRef.current = true;
      setEditorValue(html);
      editor?.commands.setContent(html);
      suppressAutosaveRef.current = false;

      // Load comments
      try {
        const caseComments = await onLoadComments(caseData.id);
        setComments(caseComments);
      } catch (error) {
        console.warn('Could not load comments:', error);
        setComments([]);
      }
      
    } catch (error) {
      console.error('Failed to load article data:', error);
      showSnackbar('Failed to load article data', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!caseData) return;

    setSaving(true);
    try {
      // Persist to backend (if supported) and to localStorage as fallback
      localStorage.setItem(localStorageKey, editorValue);

      await onSave({
        id: caseData.id,
        // Save both HTML and plain text; backend may ignore unknown fields
        case_narrative: htmlToPlainText(editorValue) || editorValue,
        // @ts-ignore
        review_html: editorValue
      });
      showSnackbar('Article review saved successfully', 'success');
    } catch (error) {
      console.error('Failed to save article review:', error);
      showSnackbar('Failed to save article review', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleAddComment = async () => {
    if (!caseData || !newComment.trim()) return;

    try {
      await onAddComment(caseData.id, newComment.trim());
      setNewComment('');
      // Reload comments
      const updatedComments = await onLoadComments(caseData.id);
      setComments(updatedComments);
      showSnackbar('Comment added successfully', 'success');
    } catch (error) {
      console.error('Failed to add comment:', error);
      showSnackbar('Failed to add comment', 'error');
    }
  };

  const handleClose = () => {
    // Do not clear editorValue to avoid overwriting localStorage/server with empty HTML
    if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current);
    setComments([]);
    setNewComment('');
    onClose();
  };

  // Load data when dialog opens
  useEffect(() => {
    if (open && caseData) {
      loadArticleData();
    }
  }, [open, caseData]); // eslint-disable-line react-hooks/exhaustive-deps

  // Simple toolbar actions for Tiptap
  const toggleBold = () => editor?.chain().focus().toggleBold().run();
  const toggleItalic = () => editor?.chain().focus().toggleItalic().run();
  const toggleStrike = () => editor?.chain().focus().toggleStrike().run();
  const toggleBullet = () => editor?.chain().focus().toggleBulletList().run();
  const toggleOrdered = () => editor?.chain().focus().toggleOrderedList().run();
  const toggleBlockquote = () => editor?.chain().focus().toggleBlockquote().run();
  const toggleCodeBlock = () => editor?.chain().focus().toggleCodeBlock().run();
  const toggleHighlight = () => editor?.chain().focus().toggleHighlight().run();
  const setHeading1 = () => editor?.chain().focus().toggleHeading({ level: 1 }).run();
  const setHeading2 = () => editor?.chain().focus().toggleHeading({ level: 2 }).run();
  const clearMarks = () => editor?.chain().focus().unsetAllMarks().run();
  const clearNodes = () => editor?.chain().focus().clearNodes().run();

  if (!caseData) return null;

  return (
    <>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: { height: '90vh' }
        }}
      >
        <DialogTitle>
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Stack direction="row" alignItems="center" spacing={2}>
              <ArticleIcon color="primary" />
              <Box>
                <Typography variant="h6" component="div">
                  Article Review - Case #{caseData.id}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {caseData.article_title || 'Untitled Article'}
                </Typography>
              </Box>
            </Stack>
            <Stack direction="row" spacing={1}>
              <Button
                variant="outlined"
                startIcon={<RefreshIcon />}
                onClick={loadArticleData}
                disabled={loading}
              >
                Refresh
              </Button>
              <IconButton onClick={handleClose}>
                <CloseIcon />
              </IconButton>
            </Stack>
          </Stack>
        </DialogTitle>

        <DialogContent dividers>
          {loading ? (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
              <CircularProgress />
              <Typography variant="body2" sx={{ ml: 2 }}>
                Loading article content...
              </Typography>
            </Box>
          ) : (
            <Stack spacing={3}>
              {/* Article Metadata */}
              <Paper sx={{ p: 2 }}>
                <Stack direction="row" spacing={1} flexWrap="wrap" alignItems="center">
                  <Chip
                    label={`Status: ${caseData.status || 'Unknown'}`}
                    color={caseData.status === 'completed' ? 'success' : 'default'}
                    size="small"
                  />
                  {caseData.ai_score && (
                    <Chip
                      label={`AI Score: ${caseData.ai_score}/3`}
                      color={caseData.ai_score >= 3 ? 'success' : caseData.ai_score >= 2 ? 'warning' : 'error'}
                      size="small"
                    />
                  )}
                  {caseData.article_year && <Chip label={caseData.article_year} size="small" />}
                  {caseData.article_venue && <Chip label={caseData.article_venue} size="small" variant="outlined" />}
                </Stack>
              </Paper>

              {/* Article Content Editor */}
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Article Content
                  </Typography>
                  <Stack spacing={1} sx={{ mb: 1 }} direction="row" flexWrap="wrap" alignItems="center">
                    <Chip size="small" label="H1" onClick={setHeading1} variant={editor?.isActive('heading', { level: 1 }) ? 'filled' : 'outlined'} />
                    <Chip size="small" label="H2" onClick={setHeading2} variant={editor?.isActive('heading', { level: 2 }) ? 'filled' : 'outlined'} />
                    <Divider flexItem orientation="vertical" />
                    <Button size="small" onClick={toggleBold} variant={editor?.isActive('bold') ? 'contained' : 'outlined'}>Bold</Button>
                    <Button size="small" onClick={toggleItalic} variant={editor?.isActive('italic') ? 'contained' : 'outlined'}>Italic</Button>
                    <Button size="small" onClick={toggleStrike} variant={editor?.isActive('strike') ? 'contained' : 'outlined'}>Strike</Button>
                    <Button size="small" onClick={toggleHighlight} color={editor?.isActive('highlight') ? 'warning' : 'primary'} variant={editor?.isActive('highlight') ? 'contained' : 'outlined'}>Highlight</Button>
                    <Divider flexItem orientation="vertical" />
                    <Button size="small" onClick={toggleBullet} variant={editor?.isActive('bulletList') ? 'contained' : 'outlined'}>Bullets</Button>
                    <Button size="small" onClick={toggleOrdered} variant={editor?.isActive('orderedList') ? 'contained' : 'outlined'}>Numbered</Button>
                    <Button size="small" onClick={toggleBlockquote} variant={editor?.isActive('blockquote') ? 'contained' : 'outlined'}>Quote</Button>
                    <Button size="small" onClick={toggleCodeBlock} variant={editor?.isActive('codeBlock') ? 'contained' : 'outlined'}>Code</Button>
                    <Divider flexItem orientation="vertical" />
                    <Button size="small" onClick={clearMarks}>Clear Marks</Button>
                    <Button size="small" onClick={clearNodes}>Clear Nodes</Button>
                    <Divider flexItem orientation="vertical" />
                    <Chip
                      size="small"
                      label={autoSaveEnabled ? 'Auto-save: On' : 'Auto-save: Off'}
                      color={autoSaveEnabled ? 'success' : 'default'}
                      onClick={() => setAutoSaveEnabled(v => !v)}
                      variant={autoSaveEnabled ? 'filled' : 'outlined'}
                    />
                  </Stack>
                  <Box sx={{ 
                    border: 1, 
                    borderColor: 'divider', 
                    borderRadius: 2,
                    minHeight: '420px',
                    bgcolor: '#fff',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.06), 0 1px 2px rgba(0,0,0,0.05)',
                    px: 1,
                    '& .tiptap': {
                      minHeight: '360px',
                      fontSize: '15px',
                      lineHeight: 1.7,
                      padding: 2.5,
                      outline: 'none',
                      backgroundColor: '#fff',
                      color: '#000',
                      caretColor: '#000',
                      fontFamily: `-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, 'Noto Sans', 'Apple Color Emoji', 'Segoe UI Emoji', 'Segoe UI Symbol', sans-serif`,
                    },
                    '& .tiptap p': {
                      margin: '0 0 10px 0',
                    },
                    '& .tiptap a': {
                      color: '#1976d2',
                      textDecoration: 'underline',
                    }
                  }}>
                    <EditorContent editor={editor} className="tiptap" />
                  </Box>
                </CardContent>
              </Card>

              {/* Comments Section */}
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Review Comments
                  </Typography>
                  
                  {/* Add Comment */}
                  <Box sx={{ mb: 2 }}>
                    <TextField
                      fullWidth
                      multiline
                      rows={3}
                      placeholder="Add your review comment..."
                      value={newComment}
                      onChange={(e) => setNewComment(e.target.value)}
                      variant="outlined"
                      size="small"
                    />
                    <Box sx={{ mt: 1, display: 'flex', justifyContent: 'flex-end' }}>
                      <Button
                        variant="contained"
                        startIcon={<CommentIcon />}
                        onClick={handleAddComment}
                        disabled={!newComment.trim()}
                        size="small"
                      >
                        Add Comment
                      </Button>
                    </Box>
                  </Box>

                  <Divider sx={{ my: 2 }} />

                  {/* Comments List */}
                  {comments.length > 0 ? (
                    <Stack spacing={2}>
                      {comments.map((comment, index) => {
                        const text = (comment && (comment.comment_text || comment.comment || comment.content || comment.text)) || '';
                        const author = comment?.user_name || comment?.author || '';
                        const created = comment?.created_at ? new Date(comment.created_at).toLocaleString() : '';
                        return (
                          <Paper key={index} sx={{ p: 2, bgcolor: alpha('#1976d2', 0.05) }}>
                            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.5 }}>
                              <Typography variant="body2" color="text.secondary">
                                {created || 'Unknown date'}
                              </Typography>
                              {author && (
                                <Chip size="small" label={author} variant="outlined" />
                              )}
                            </Stack>
                            <Typography variant="body1">
                              {text || 'No content'}
                            </Typography>
                          </Paper>
                        );
                      })}
                    </Stack>
                  ) : (
                    <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                      No comments yet. Be the first to add a review comment.
                    </Typography>
                  )}
                </CardContent>
              </Card>
            </Stack>
          )}
        </DialogContent>

        <DialogActions>
          <Button onClick={handleClose} disabled={saving}>
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            variant="contained"
            startIcon={<SaveIcon />}
            disabled={saving || loading}
          >
            {saving ? 'Saving...' : 'Save Review'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default ArticleReviewEditor;